import { c as _c } from "react/compiler-runtime";
import type { OnboardingDataEntry } from '@onefootprint/request-types/dashboard';
import { CodeBlock, MarkdownText, useObjectUrl } from '@onefootprint/ui';
import dynamic from 'next/dynamic';
import { useTranslation } from 'react-i18next';
import { GenUiBlockRenderer, getSchemaVersion } from '@/features/gen-ui';
import { decodeBase64, getFileMetadata } from '../utils/file-artifacts-utils';
import ExpandableText from './expandable-text';
const PdfViewer = dynamic(() => import('@/features/document-viewer/components/document-preview/components/pdf-viewer'), {
  ssr: false
});
type FileArtifactViewProps = {
  artifact: OnboardingDataEntry;
};
const FileArtifactView = t0 => {
  const $ = _c(25);
  const {
    artifact
  } = t0;
  const {
    t
  } = useTranslation("onboarding-data-entry-viewer");
  const {
    isFile,
    mimeType,
    fileName,
    base64Value
  } = getFileMetadata(artifact);
  const {
    objectUrl
  } = useObjectUrl(base64Value);
  if (!artifact.schema || !isFile) {
    let t1;
    if ($[0] !== t) {
      t1 = t(_temp);
      $[0] = t;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    let t2;
    if ($[2] !== t1) {
      t2 = <div className="flex items-center justify-center h-full p-4 text-secondary"><p>{t1}</p></div>;
      $[2] = t1;
      $[3] = t2;
    } else {
      t2 = $[3];
    }
    return t2;
  }
  if (mimeType === "application/jsonl" && fileName.startsWith("ui_block_")) {
    let t1;
    if ($[4] !== artifact.value) {
      t1 = typeof artifact.value === "string" ? artifact.value : JSON.stringify(artifact.value, null, 2);
      $[4] = artifact.value;
      $[5] = t1;
    } else {
      t1 = $[5];
    }
    const contentString = t1;
    const metadata = artifact.schema?.kind === "file" ? artifact.schema.data.metadata : undefined;
    let t2;
    if ($[6] !== metadata) {
      t2 = getSchemaVersion(metadata);
      $[6] = metadata;
      $[7] = t2;
    } else {
      t2 = $[7];
    }
    let t3;
    if ($[8] !== contentString || $[9] !== t2) {
      t3 = <GenUiBlockRenderer content={contentString} schemaVersion={t2} />;
      $[8] = contentString;
      $[9] = t2;
      $[10] = t3;
    } else {
      t3 = $[10];
    }
    return t3;
  }
  if (mimeType === "application/json") {
    let t1;
    if ($[11] !== artifact.value) {
      const jsonString = typeof artifact.value === "string" ? artifact.value : JSON.stringify(artifact.value, null, 2);
      try {
        t1 = JSON.stringify(JSON.parse(jsonString), null, 2);
      } catch {
        t1 = jsonString;
      }
      $[11] = artifact.value;
      $[12] = t1;
    } else {
      t1 = $[12];
    }
    const formatted = t1;
    let t2;
    if ($[13] !== formatted) {
      t2 = <div className="h-full overflow-auto [&>div]:border-0 [&>div]:rounded-none"><CodeBlock language="json" hideHeader={true} showLineNumbers={true}>{formatted}</CodeBlock></div>;
      $[13] = formatted;
      $[14] = t2;
    } else {
      t2 = $[14];
    }
    return t2;
  }
  if (mimeType.startsWith("image/")) {
    const imageSrc = objectUrl ?? `data:${mimeType};base64,${base64Value}`;
    return <div className="h-full w-full overflow-auto"><img src={imageSrc} alt={fileName} className="w-full h-auto" /></div>;
  }
  if (mimeType === "application/pdf" && base64Value) {
    const t1 = `data:application/pdf;base64,${base64Value}`;
    let t2;
    let t3;
    if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = [];
      t3 = new Set();
      $[15] = t2;
      $[16] = t3;
    } else {
      t2 = $[15];
      t3 = $[16];
    }
    let t4;
    if ($[17] !== t1) {
      t4 = <div className="h-full"><PdfViewer src={t1} bboxes={t2} selectedBboxIds={t3} onBboxClick={_temp2} /></div>;
      $[17] = t1;
      $[18] = t4;
    } else {
      t4 = $[18];
    }
    return t4;
  }
  if (mimeType.startsWith("text/") && typeof artifact.value === "string") {
    let t1;
    if ($[19] !== artifact.value) {
      t1 = decodeBase64(artifact.value);
      $[19] = artifact.value;
      $[20] = t1;
    } else {
      t1 = $[20];
    }
    const decodedValue = t1;
    const value = decodedValue ?? artifact.value;
    if (mimeType === "text/markdown") {
      let t2;
      if ($[21] !== value) {
        t2 = <div className="h-full overflow-auto p-4"><MarkdownText>{value}</MarkdownText></div>;
        $[21] = value;
        $[22] = t2;
      } else {
        t2 = $[22];
      }
      return t2;
    }
    return <div className="h-full"><ExpandableText value={value} filename={fileName} /></div>;
  }
  const t1 = "flex items-center justify-center h-full p-4 text-secondary";
  const t2 = t(_temp3, {
    type: mimeType || t(_temp4)
  });
  let t3;
  if ($[23] !== t2) {
    t3 = <div className={t1}><p>{t2}</p></div>;
    $[23] = t2;
    $[24] = t3;
  } else {
    t3 = $[24];
  }
  return t3;
};
export default FileArtifactView;
function _temp($) {
  return $.selectFile;
}
function _temp2() {}
function _temp3($_0) {
  return $_0.previewNotAvailable;
}
function _temp4($_1) {
  return $_1.unknownType;
}